import { apiClient } from "./client";

const BACKEND_API_BASE_URL = import.meta.env.VITE_BACKEND_API_BASE_URL ?? "";

const getDashboardReportPath = () =>
  /\/v1\/?$/.test(BACKEND_API_BASE_URL)
    ? "/dashboard/report"
    : "/v1/dashboard/report";

export type DashboardTone =
  | "blue"
  | "green"
  | "amber"
  | "rose"
  | "purple"
  | "orange";

export type DashboardIconName =
  | "bot"
  | "calendarDays"
  | "clock"
  | "phone"
  | "phoneMissed"
  | "target"
  | "trendingUp"
  | "user"
  | "userPlus"
  | "users";

export interface DashboardMetric {
  metricId: string;
  metricTitle: string;
  metricValue: string;
  trendDirection: "up" | "down" | "flat";
  trendPercentage: string;
  comparisonLabel: string;
  iconName: DashboardIconName;
  colorTone: DashboardTone;
}

export interface DailyPerformance {
  dateLabel: string;
  completedCalls: number;
}

export interface PerformanceSummaryCard {
  summaryId: string;
  summaryTitle: string;
  summaryValue: string;
  summaryDetail: string;
  iconName: DashboardIconName;
  colorTone: DashboardTone;
}

export interface DateRangeOption {
  dateRangeId: string;
  dateRangeLabel: string;
  isSelected: boolean;
}

export interface RecentActivity {
  activityId: string;
  activityTitle: string;
  activityDescription: string;
  activityTime: string;
  statusLabel: string;
  iconName: DashboardIconName;
  colorTone: DashboardTone;
}

export interface UpcomingAppointment {
  appointmentId: string;
  customerInitials: string;
  customerName: string;
  scheduledDateTimeLabel: string;
  assignedAgentName: string;
  appointmentType: string;
  statusLabel: string;
}

export interface QuickAction {
  actionId: string;
  actionTitle: string;
  actionDescription: string;
  routePath: string;
  iconName: DashboardIconName;
  colorTone: DashboardTone;
}

export interface AdminDashboardData {
  reportMetadata: {
    reportName: string;
    reportDescription: string;
    generatedAt: string;
    dateRangeLabel: string;
  };
  dashboardMetrics: DashboardMetric[];
  agentPerformance: {
    sectionTitle: string;
    dateRangeLabel: string;
    availableDateRanges: DateRangeOption[];
    chartMaximumValue: number;
    yAxisScaleLabels: number[];
    dailyPerformance: DailyPerformance[];
    summaryCards: PerformanceSummaryCard[];
  };
  recentActivities: RecentActivity[];
  upcomingAppointments: UpcomingAppointment[];
  quickActions: QuickAction[];
}

export interface AdminDashboardReportResponse {
  success: boolean;
  message?: string;
  data: AdminDashboardData;
}

interface ApiDashboardMetric {
  id: string;
  title: string;
  value: number | string;
  trend: "up" | "down" | "flat";
  pct: number | string;
  label: string;
}

interface ApiDailyTrend {
  date: string;
  label: string;
  calls: number;
}

interface ApiPerformanceSummary {
  id: string;
  title: string;
  value: number | string;
  detail: string;
}

interface ApiRecentActivity {
  id: string;
  title: string;
  desc: string;
  timestamp: string;
  status: string;
}

interface ApiUpcomingAppointment {
  id?: string;
  appointmentId?: string;
  customerInitials?: string;
  customerName?: string;
  name?: string;
  scheduledDateTimeLabel?: string;
  scheduled_at?: string;
  scheduledAt?: string;
  assignedAgentName?: string;
  agentName?: string;
  appointmentType?: string;
  type?: string;
  statusLabel?: string;
  status?: string;
}

interface ApiDashboardData {
  metrics: ApiDashboardMetric[];
  performance: {
    max_scale: number;
    y_axis: number[];
    daily_trend: ApiDailyTrend[];
    summary: ApiPerformanceSummary[];
  };
  recent_activities: ApiRecentActivity[];
  upcoming_appointments: ApiUpcomingAppointment[];
}

type ApiOrAdminDashboardData = ApiDashboardData | AdminDashboardData;

const metricPresentation: Record<
  string,
  { iconName: DashboardIconName; colorTone: DashboardTone }
> = {
  total_agents: { iconName: "users", colorTone: "blue" },
  calls_today: { iconName: "phone", colorTone: "rose" },
  appointments_today: { iconName: "calendarDays", colorTone: "blue" },
  total_leads: { iconName: "trendingUp", colorTone: "amber" },
};

const summaryPresentation: Record<
  string,
  { iconName: DashboardIconName; colorTone: DashboardTone }
> = {
  active_agents: { iconName: "users", colorTone: "green" },
  average_response_time: { iconName: "clock", colorTone: "blue" },
  conversion_rate: { iconName: "target", colorTone: "purple" },
};

const quickActions: QuickAction[] = [
  {
    actionId: "add_ai_agent",
    actionTitle: "Add AI-Agent",
    actionDescription: "Create a new AI-Agent",
    routePath: "/add-ai-agent",
    iconName: "bot",
    colorTone: "blue",
  },
  {
    actionId: "manage_ai_agents",
    actionTitle: "Manage AI-Agents",
    actionDescription: "View and manage agents",
    routePath: "/ai-agents",
    iconName: "users",
    colorTone: "green",
  },
  {
    actionId: "view_calendar",
    actionTitle: "View Calendar",
    actionDescription: "Check appointments",
    routePath: "/calendar",
    iconName: "calendarDays",
    colorTone: "amber",
  },
  {
    actionId: "history_logs",
    actionTitle: "History Logs",
    actionDescription: "View call & activity logs",
    routePath: "/auto-dialer",
    iconName: "phone",
    colorTone: "orange",
  },
];

const isAdminDashboardData = (
  data: ApiOrAdminDashboardData,
): data is AdminDashboardData =>
  "dashboardMetrics" in data && "agentPerformance" in data;

const formatNumber = (value: number | string) =>
  typeof value === "number" ? new Intl.NumberFormat().format(value) : value;

const formatPercentage = (value: number | string) => {
  if (typeof value === "string") {
    return value.includes("%") ? value : `${value}%`;
  }

  return `${value}%`;
};

const deriveDateRangeLabel = (metrics: ApiDashboardMetric[]) => {
  const label = metrics.find((metric) => metric.label)?.label;

  if (!label) {
    return "Current Period";
  }

  return label.replace(/^vs\s+/i, "").replace(/^./, (char) =>
    char.toUpperCase(),
  );
};

const formatActivityTime = (timestamp: string) => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "NA";

const formatAppointmentDateTime = (appointment: ApiUpcomingAppointment) => {
  const explicitLabel = appointment.scheduledDateTimeLabel;

  if (explicitLabel) {
    return explicitLabel;
  }

  const timestamp = appointment.scheduled_at || appointment.scheduledAt;

  if (!timestamp) {
    return "Not scheduled";
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizeDashboardData = (
  data: ApiOrAdminDashboardData,
): AdminDashboardData => {
  if (isAdminDashboardData(data)) {
    return data;
  }

  const dateRangeLabel = deriveDateRangeLabel(data.metrics);

  return {
    reportMetadata: {
      reportName: "Admin Dashboard",
      reportDescription: "Live dashboard report.",
      generatedAt: new Date().toISOString(),
      dateRangeLabel,
    },
    dashboardMetrics: data.metrics.map((metric) => {
      const presentation = metricPresentation[metric.id] ?? {
        iconName: "trendingUp" as const,
        colorTone: "amber" as const,
      };

      return {
        metricId: metric.id,
        metricTitle: metric.title,
        metricValue: formatNumber(metric.value),
        trendDirection: metric.trend,
        trendPercentage: formatPercentage(metric.pct),
        comparisonLabel: metric.label,
        ...presentation,
      };
    }),
    agentPerformance: {
      sectionTitle: "Agent Performance",
      dateRangeLabel,
      availableDateRanges: [
        {
          dateRangeId: "current_period",
          dateRangeLabel,
          isSelected: true,
        },
      ],
      chartMaximumValue: data.performance.max_scale || 100,
      yAxisScaleLabels: data.performance.y_axis,
      dailyPerformance: data.performance.daily_trend.map((item) => ({
        dateLabel: item.label,
        completedCalls: item.calls,
      })),
      summaryCards: data.performance.summary.map((item) => {
        const presentation = summaryPresentation[item.id] ?? {
          iconName: "target" as const,
          colorTone: "blue" as const,
        };

        return {
          summaryId: item.id,
          summaryTitle: item.title,
          summaryValue: formatNumber(item.value),
          summaryDetail: item.detail,
          ...presentation,
        };
      }),
    },
    recentActivities: data.recent_activities.map((item) => ({
      activityId: item.id,
      activityTitle: item.title,
      activityDescription: item.desc,
      activityTime: formatActivityTime(item.timestamp),
      statusLabel: item.status,
      iconName: item.status.toLowerCase().includes("missed")
        ? "phoneMissed"
        : "phone",
      colorTone: item.status.toLowerCase().includes("missed")
        ? "orange"
        : "green",
    })),
    upcomingAppointments: data.upcoming_appointments.map((item, index) => {
      const customerName = item.customerName || item.name || "Customer";

      return {
        appointmentId:
          item.appointmentId || item.id || `appointment_${index + 1}`,
        customerInitials: item.customerInitials || getInitials(customerName),
        customerName,
        scheduledDateTimeLabel: formatAppointmentDateTime(item),
        assignedAgentName:
          item.assignedAgentName || item.agentName || "Unassigned",
        appointmentType: item.appointmentType || item.type || "Appointment",
        statusLabel: item.statusLabel || item.status || "Scheduled",
      };
    }),
    quickActions,
  };
};

class DashboardService {
  private readonly reportPath = getDashboardReportPath();

  async getReport(): Promise<AdminDashboardReportResponse> {
    const response =
      await apiClient.get<ApiOrAdminDashboardData>(this.reportPath);

    return {
      ...response,
      data: normalizeDashboardData(response.data),
    };
  }
}

export const dashboardService = new DashboardService();
